import type { ReactNode } from 'react'

import { ModalView as EcloudModal } from '@enechain/ecloud-designsystem'

export type ModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full'

type Props = {
  isOpen: boolean
  onClose: () => void
  title: string
  size?: ModalSize
  children: ReactNode
  footer?: ReactNode
  /**
   * スクロール挙動。 'inside' は body のみスクロールさせ header / footer を固定する
   * (= フォーム項目が多い modal で作成ボタンを下部固定にする用途)。 既定は
   * デフォルトの 'outside' (= modal 全体がオーバーレイ内でスクロール)。
   */
  scrollBehavior?: 'inside' | 'outside'
  /**
   * Escape キーで閉じるか。既定は true。spec が「× ボタンでのみ閉じる」と定める
   * modal (例: 連絡先モーダル AC-CM-003) だけ false にする。
   */
  closeOnEscape?: boolean
}

/**
 * 共通 Modal コンポーネント。
 *
 * ecloud-designsystem の `Modal` (= ModalView) を wrap し、overlay / content の
 * スタイル (white bg / radius / shadow / overlay 透過) は DS 側に委ねる。
 *
 * AC-014 準拠: オーバーレイクリックでは閉じない (closeOnInteractOutside={false})。
 * Escape キーは既定で有効 (`closeOnEscape` で無効化できる)。明示的な閉じる手段: × ボタン / footer の Cancel / Save。
 *
 * 縦位置は `placement="top"` で上寄せ + 固定オフセットに固定する
 * (`design/CLAUDE.md`「縦位置は上寄せ + 固定オフセット。中央寄せにしない (#3731)」。
 * design プロト実測の上余白 72px に対し DS の `top` は 64px)。全モーダル共通。
 * ただし同じ design/CLAUDE.md の但し書きどおり、縦が狭い端末 (`max-height: 640px`) だけは
 * 中央寄せへフォールバックする。 chakra の breakpoint は幅基準で高さ条件を書けないため、
 * この分岐は `~/constants/system.ts` の globalCss (media query) 側に置いている。
 *
 * 15 タブ × CRUD で再利用することを前提に設計。
 */
export const Modal = ({
  isOpen,
  onClose,
  title,
  size = 'md',
  children,
  footer,
  scrollBehavior,
  closeOnEscape = true,
}: Props): React.ReactElement => {
  return (
    <EcloudModal
      bodyChildren={children}
      closeOnEscape={closeOnEscape}
      closeOnInteractOutside={false}
      footerChildren={footer ?? null}
      onClose={onClose}
      open={isOpen}
      placement="top"
      scrollBehavior={scrollBehavior}
      size={size}
      title={title}
    />
  )
}
